import React from 'react';
import { Container, Row, Col, Card, Badge, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Book, GraphUp, Award, CheckCircle, ExclamationTriangle, Facebook, Twitter, Instagram, Linkedin } from 'react-bootstrap-icons';
import logo from '../assets/logo.png';
import whiteLogo from '../assets/whitebglogo.png';
import androidQrCode from '../assets/playstoreqr.png';
import iosQrCode from '../assets/iosqrcode.png';
import SEOHead from './SEOHead';
import '../App.css';

const ClassGuide = () => {
  const { grade } = useParams();
  const navigate = useNavigate();

  const validGrades = ['9', '10', '11', '12'];
  const isValidGrade = validGrades.includes(grade);

  React.useEffect(() => {
    if (!isValidGrade) {
      navigate('/');
    }
  }, [grade, navigate, isValidGrade]);

  if (!isValidGrade) {
    return null;
  }

  const getGradeData = () => {
    const data = {
      '9': {
        title: 'Class 9 Study Guide',
        subtitle: 'Master the fundamentals and build a strong foundation for your academic journey',
        description: 'Class 9 is a crucial year that sets the foundation for your future academic success. This comprehensive guide will help you navigate through all subjects with effective study strategies, exam preparation tips, and important dates.',
        examType: 'Annual School Exams',
        keySubjects: ['Mathematics', 'Science', 'Social Studies', 'English', 'Hindi'],
        examDates: 'March-April 2024',
        difficulty: 'Foundation Level',
        importance: 'Critical for Class 10 preparation',
        tips: [
          {
            title: 'Conceptual Understanding',
            description: 'Focus on understanding concepts rather than memorization. Build strong conceptual foundations that will help in higher classes.',
            icon: '🧠'
          },
          {
            title: 'NCERT Mastery',
            description: 'Practice NCERT exercises thoroughly. NCERT textbooks are the foundation - master them completely before moving to reference books.',
            icon: '📚'
          },
          {
            title: 'Consistent Schedule',
            description: 'Maintain regular study schedule. Consistency is key - even 2-3 hours daily is better than cramming on weekends.',
            icon: '⏰'
          },
          {
            title: 'Practice Papers',
            description: 'Solve sample papers and previous year questions. This helps understand exam pattern and improves time management.',
            icon: '📝'
          },
          {
            title: 'Balanced Approach',
            description: 'Take regular breaks to avoid burnout. Balance studies with physical activity and hobbies for better mental health.',
            icon: '⚖️'
          }
        ],
        strategies: [
          {
            title: 'Time Management',
            description: 'Create a weekly study timetable allocating specific time slots for each subject based on difficulty level.',
            icon: '📅'
          },
          {
            title: 'Visual Learning',
            description: 'Use visual aids like diagrams, charts, and mind maps. Visual learning helps in better retention and understanding.',
            icon: '🎨'
          },
          {
            title: 'Group Studies',
            description: 'Form study groups with classmates. Teaching others helps reinforce your own understanding and clarifies doubts.',
            icon: '👥'
          },
          {
            title: 'Regular Revision',
            description: 'Regular revision of completed topics. Follow the 1-3-7-21 day revision cycle for better retention.',
            icon: '🔄'
          },
          {
            title: 'Seek Guidance',
            description: 'Seek help from teachers when needed. Don\'t hesitate to ask questions - it shows your commitment to learning.',
            icon: '🙋‍♂️'
          }
        ],
        examPattern: {
          duration: '3 hours',
          totalMarks: '80 marks',
          sections: ['Objective (20 marks)', 'Short Answer (30 marks)', 'Long Answer (30 marks)'],
          passingMarks: '33%'
        },
        preparationTimeline: [
          { month: 'April-June', focus: 'Understanding new concepts', activities: ['Read NCERT chapters', 'Solve basic exercises', 'Clear fundamental doubts'] },
          { month: 'July-September', focus: 'Practice and application', activities: ['Solve advanced problems', 'Take mock tests', 'Identify weak areas'] },
          { month: 'October-December', focus: 'Revision and testing', activities: ['Complete syllabus', 'Solve sample papers', 'Regular revision'] },
          { month: 'January-March', focus: 'Final preparation', activities: ['Final revision', 'Previous year papers', 'Exam preparation'] }
        ]
      },
      '10': {
        title: 'Class 10 Board Exam Guide',
        subtitle: 'Excel in your first major academic milestone with comprehensive preparation strategies',
        description: 'Class 10 board exams are your first major academic milestone. This guide provides detailed strategies for exam preparation, important dates, and tips to score well in all subjects.',
        examType: 'CBSE Board Exams',
        keySubjects: ['Mathematics', 'Science', 'Social Studies', 'English', 'Hindi'],
        examDates: 'February-March 2024',
        difficulty: 'Board Level',
        importance: 'First major academic milestone',
        tips: [
          {
            title: 'Early Preparation',
            description: 'Start preparation early - at least 6 months before exams. Early start gives you time to cover syllabus thoroughly and revise multiple times.',
            icon: '🚀'
          },
          {
            title: 'NCERT Focus',
            description: 'Focus on NCERT textbooks as primary resource. 90% of board questions come directly from NCERT - master every line and diagram.',
            icon: '📖'
          },
          {
            title: 'Previous Papers',
            description: 'Practice previous 10 years board papers. This helps understand question patterns, marking schemes, and common topics.',
            icon: '📊'
          },
          {
            title: 'Time Management',
            description: 'Time management during exams is crucial. Practice writing answers within time limits to avoid leaving questions unanswered.',
            icon: '⏱️'
          },
          {
            title: 'Health & Wellness',
            description: 'Maintain good health and sleep schedule. Physical and mental health directly impacts your performance and concentration.',
            icon: '💪'
          }
        ],
        strategies: [
          {
            title: 'Subject Planning',
            description: 'Create subject-wise study plan allocating time based on difficulty and your strengths. Give more time to challenging subjects.',
            icon: '📋'
          },
          {
            title: 'Answer Writing',
            description: 'Practice writing answers within time limit. Board exams test not just knowledge but also presentation and time management skills.',
            icon: '✍️'
          },
          {
            title: 'Mock Tests',
            description: 'Regular mock tests and self-assessment. Take full-length tests under exam conditions to build confidence and identify gaps.',
            icon: '🎯'
          },
          {
            title: 'Formula Mastery',
            description: 'Revision of important formulas and concepts. Create formula sheets and revise them daily for quick recall during exams.',
            icon: '🧮'
          },
          {
            title: 'Stress Management',
            description: 'Develop stress management techniques. Practice meditation, breathing exercises, and maintain positive mindset throughout preparation.',
            icon: '🧘‍♀️'
          }
        ],
        examPattern: {
          duration: '3 hours',
          totalMarks: '80 marks',
          sections: ['Objective (20 marks)', 'Short Answer (30 marks)', 'Long Answer (30 marks)'],
          passingMarks: '33%'
        },
        preparationTimeline: [
          { month: 'April-June', focus: 'Foundation building', activities: ['Complete NCERT reading', 'Solve basic exercises', 'Clear concepts'] },
          { month: 'July-September', focus: 'Practice phase', activities: ['Advanced problem solving', 'Previous year papers', 'Mock tests'] },
          { month: 'October-December', focus: 'Revision phase', activities: ['Complete syllabus', 'Regular revision', 'Sample papers'] },
          { month: 'January-March', focus: 'Final preparation', activities: ['Final revision', 'Exam strategy', 'Board exams'] }
        ]
      },
      '11': {
        title: 'Class 11 Study Guide',
        subtitle: 'Transition smoothly to higher secondary with specialized study approaches',
        description: 'Class 11 marks the beginning of your specialization journey. Choose your stream wisely and develop effective study habits for Science, Commerce, or Arts streams.',
        examType: 'Annual School Exams',
        keySubjects: ['Physics', 'Chemistry', 'Mathematics/Biology', 'English', 'Optional Subject'],
        examDates: 'March-April 2024',
        difficulty: 'Intermediate Level',
        importance: 'Foundation for Class 12 and competitive exams',
        tips: [
          {
            title: 'Stream Selection',
            description: 'Choose your stream based on interest, aptitude, and career goals. Research thoroughly before making this crucial decision.',
            icon: '🎯'
          },
          {
            title: 'Conceptual Foundation',
            description: 'Focus on building strong conceptual understanding. Class 11 concepts are fundamental for Class 12 and competitive exams.',
            icon: '🧠'
          },
          {
            title: 'Regular Practice',
            description: 'Practice numerical problems and derivations regularly. Consistent practice is key to mastering complex topics.',
            icon: '📝'
          },
          {
            title: 'Beyond Textbooks',
            description: 'Read reference books and additional materials. NCERT is important but explore other resources for deeper understanding.',
            icon: '📚'
          },
          {
            title: 'Balanced Routine',
            description: 'Maintain a consistent study routine while balancing academics with extracurricular activities and personal interests.',
            icon: '⚖️'
          }
        ],
        strategies: [
          {
            title: 'Stream-Specific Approach',
            description: 'Adapt your study methods based on your chosen stream. Science requires more problem-solving, Commerce needs analytical skills, Arts focuses on critical thinking.',
            icon: '📊'
          },
          {
            title: 'Practical Work',
            description: 'Regular practice of practical work and laboratory sessions. Hands-on experience is crucial for understanding theoretical concepts.',
            icon: '🔬'
          },
          {
            title: 'Competitive Preparation',
            description: 'If planning for competitive exams, start early preparation alongside board exam studies. Join coaching if needed.',
            icon: '🏆'
          },
          {
            title: 'Analytical Thinking',
            description: 'Develop analytical and critical thinking skills. These are essential for higher studies and professional life.',
            icon: '💡'
          },
          {
            title: 'Time Management',
            description: 'Learn to manage time effectively. Create study schedules that accommodate all subjects and activities.',
            icon: '⏰'
          }
        ],
        examPattern: {
          duration: '3 hours',
          totalMarks: '80 marks',
          sections: ['Objective (20 marks)', 'Short Answer (30 marks)', 'Long Answer (30 marks)'],
          passingMarks: '33%'
        },
        preparationTimeline: [
          { month: 'April-June', focus: 'Stream adaptation and foundation', activities: ['Understand new subjects', 'Adapt to stream requirements', 'Build conceptual foundation'] },
          { month: 'July-September', focus: 'Deep learning and practice', activities: ['Master complex topics', 'Regular practice sessions', 'Practical work'] },
          { month: 'October-December', focus: 'Advanced preparation', activities: ['Solve advanced problems', 'Mock tests', 'Identify weak areas'] },
          { month: 'January-March', focus: 'Final preparation', activities: ['Complete syllabus', 'Final revision', 'Exam preparation'] }
        ]
      },
      '12': {
        title: 'Class 12 Board Exam Guide',
        subtitle: 'Ace your final board exams and achieve your academic goals',
        description: 'Class 12 board exams are the most important exams of your school life. This comprehensive guide will help you prepare effectively, manage time, and perform your best.',
        examType: 'CBSE Board Exams',
        keySubjects: ['Physics', 'Chemistry', 'Mathematics/Biology', 'English', 'Optional Subject'],
        examDates: 'February-March 2024',
        difficulty: 'Advanced Level',
        importance: 'Determines college admissions and career paths',
        tips: [
          {
            title: 'Early Start',
            description: 'Start preparation from day one of Class 12. These exams determine your future, so every day counts.',
            icon: '🚀'
          },
          {
            title: 'NCERT Focus',
            description: 'Master NCERT textbooks thoroughly. Most board exam questions are directly or indirectly from NCERT.',
            icon: '📚'
          },
          {
            title: 'Previous Year Papers',
            description: 'Practice previous year papers extensively. This helps understand exam pattern and builds confidence.',
            icon: '📝'
          },
          {
            title: 'Health Management',
            description: 'Maintain physical and mental health. Regular exercise, proper sleep, and stress management are crucial.',
            icon: '💪'
          },
          {
            title: 'Positive Mindset',
            description: 'Stay positive and confident. Believe in your preparation and maintain a calm, focused approach.',
            icon: '😊'
          }
        ],
        strategies: [
          {
            title: 'Detailed Planning',
            description: 'Create a detailed study schedule covering all subjects. Allocate time based on difficulty and importance of topics.',
            icon: '📅'
          },
          {
            title: 'Subject-Specific Approach',
            description: 'Develop subject-wise preparation strategies. Each subject requires different study methods and techniques.',
            icon: '📊'
          },
          {
            title: 'Regular Assessments',
            description: 'Take regular practice tests and assessments. This helps identify weak areas and track progress.',
            icon: '📈'
          },
          {
            title: 'Exam Time Management',
            description: 'Practice time management during exams. Learn to allocate time wisely across different sections.',
            icon: '⏰'
          },
          {
            title: 'Career Planning',
            description: 'Plan your post-exam career path. Research colleges, courses, and career options based on your interests.',
            icon: '🎯'
          }
        ],
        examPattern: {
          duration: '3 hours',
          totalMarks: '80 marks',
          sections: ['Objective (20 marks)', 'Short Answer (30 marks)', 'Long Answer (30 marks)'],
          passingMarks: '33%'
        },
        preparationTimeline: [
          { month: 'April-June', focus: 'Foundation building', activities: ['Complete NCERT chapters', 'Build strong concepts', 'Regular practice'] },
          { month: 'July-September', focus: 'Advanced preparation', activities: ['Reference books', 'Previous year papers', 'Mock tests'] },
          { month: 'October-December', focus: 'Intensive revision', activities: ['Complete syllabus', 'Regular revision', 'Sample papers'] },
          { month: 'January-March', focus: 'Final preparation', activities: ['Final revision', 'Exam strategy', 'Board exams'] }
        ]
      }
    };
    return data[grade];
  };

  const gradeData = getGradeData();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalResource",
    "name": `${gradeData.title}`,
    "description": gradeData.description,
    "url": `https://cbseresources.com/guide/${grade}`,
    "educationalLevel": `Class ${grade}`,
    "learningResourceType": "Study Guide",
    "educationalUse": "study",
    "audience": {
      "@type": "EducationalAudience",
      "educationalRole": "student"
    },
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Cubegon",
      "url": "https://cbseresources.com"
    }
  };

  const StudyTipCard = ({ tip, type = 'tip' }) => (
    <Card className="study-tip-card h-100">
      <Card.Body>
        <div className="tip-icon">
          <span className="tip-emoji">{tip.icon}</span>
        </div>
        <h5 className="tip-title">{tip.title}</h5>
        <p className="tip-description">{tip.description}</p>
      </Card.Body>
    </Card>
  );

  const TimelineItem = ({ item }) => (
    <div className="timeline-item">
      <div className="timeline-marker"></div>
      <div className="timeline-content">
        <h6 className="timeline-month">{item.month}</h6>
        <p className="timeline-focus">{item.focus}</p>
        <ul className="timeline-events">
          {item.activities.map((activity, index) => (
            <li key={index}>{activity}</li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <div className="modern-guide-page">
      <SEOHead
        title={`${gradeData.title} - CBSE Study Guide | Cubegon`}
        description={gradeData.description}
        keywords={`class ${grade} CBSE, ${gradeData.title}, CBSE study guide, CBSE exam preparation, class ${grade} study materials, CBSE syllabus, NCERT solutions`}
        canonical={`/guide/${grade}`}
        structuredData={structuredData}
      />
      <Container fluid className="px-4 px-lg-5">
        {/* Header Section */}
        <section className="guide-header-section">
          <Container>
            <div className="guide-header-card">
              <div className="guide-header-content">
                <Badge className="guide-badge mb-3">Complete Study Guide</Badge>
                <h1 className="guide-title">{gradeData.title}</h1>
                <p className="guide-subtitle">{gradeData.subtitle}</p>
                <p className="guide-description">{gradeData.description}</p>
                
                <div className="guide-meta-info">
                  <div className="meta-item">
                    <Calendar className="me-2" />
                    <span><strong>Exam Type:</strong> {gradeData.examType}</span>
                  </div>
                  <div className="meta-item">
                    <Clock className="me-2" />
                    <span><strong>Exam Dates:</strong> {gradeData.examDates}</span>
                  </div>
                  <div className="meta-item">
                    <GraphUp className="me-2" />
                    <span><strong>Difficulty:</strong> {gradeData.difficulty}</span>
                  </div>
                  <div className="meta-item">
                    <Award className="me-2" />
                    <span><strong>Importance:</strong> {gradeData.importance}</span>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Key Subjects Section */}
        <section className="subjects-section">
          <Container>
            <h2 className="section-title">Key Subjects</h2>
            <Row>
              {gradeData.keySubjects.map((subject, index) => (
                <Col md={6} lg={4} key={index}>
                  <Card className="subject-card mt-4">
                    <Card.Body className="text-center">
                      <Book className="subject-icon mb-3" />
                      <h5 className="subject-name">{subject}</h5>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Exam Pattern Section */}
        <section className="exam-pattern-section">
          <Container>
            <h2 className="section-title">Exam Pattern</h2>
            <Row>
              <Col md={6} lg={3}>
                <Card className="pattern-card mt-4">
                  <Card.Body className="text-center">
                    <Clock className="pattern-icon mb-3" />
                    <h5 className="pattern-title">Duration</h5>
                    <p className="pattern-value">{gradeData.examPattern.duration}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="pattern-card mt-4">
                  <Card.Body className="text-center">
                    <Award className="pattern-icon mb-3" />
                    <h5 className="pattern-title">Total Marks</h5>
                    <p className="pattern-value">{gradeData.examPattern.totalMarks}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="pattern-card mt-4">
                  <Card.Body className="text-center">
                    <CheckCircle className="pattern-icon mb-3" />
                    <h5 className="pattern-title">Passing Marks</h5>
                    <p className="pattern-value">{gradeData.examPattern.passingMarks}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} lg={3}>
                <Card className="pattern-card mt-4">
                  <Card.Body className="text-center">
                    <Book className="pattern-icon mb-3" />
                    <h5 className="pattern-title">Sections</h5>
                    <p className="pattern-value">{gradeData.examPattern.sections.length}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Study Tips Section */}
        <section className="tips-section">
          <Container>
            <h2 className="section-title">Essential Study Tips</h2>
            <Row className="g-4">
              {gradeData.tips.map((tip, index) => (
                <Col md={6} lg={4} key={index}>
                  <StudyTipCard
                    tip={tip}
                    type="tip"
                  />
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Study Strategies Section */}
        <section className="strategies-section">
          <Container>
            <h2 className="section-title">Effective Study Strategies</h2>
            <Row className="g-4">
              {gradeData.strategies.map((strategy, index) => (
                <Col md={6} lg={4} key={index}>
                  <StudyTipCard
                    tip={strategy}
                    type="strategy"
                  />    
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Exam Timeline Section */}
        <section className="timeline-section">
          <Container>
            <h2 className="section-title">Academic Year Timeline</h2>
            <div className="timeline-container">
              {gradeData.preparationTimeline.map((item, index) => (
                <TimelineItem key={index} item={item} />
              ))}
            </div>
          </Container>
        </section>

        {/* Detailed Study Guide Section */}
        <section className="detailed-guide-section">
          <Container>
            <h2 className="section-title">Comprehensive Study Guide</h2>
            <Row>
              <Col lg={8}>
                <Card className="guide-content-card">
                  <Card.Body>
                    <h3 className="content-title">Understanding Class {grade} Curriculum</h3>
                    <p className="content-text">
                      Class {grade} serves as a crucial foundation in your academic journey. The curriculum is designed to build upon previous knowledge while introducing more complex concepts that will be essential for higher studies. At Cubegon, we understand that mastering these fundamentals is key to effective preparation and long-term academic success.
                    </p>
                    
                    <h4 className="content-subtitle">Subject-wise Preparation Strategy</h4>
                    <div className="subject-strategy">
                      <h5>Mathematics</h5>
                      <p>Mathematics requires consistent practice and conceptual clarity. Focus on understanding theorems and formulas rather than memorizing them. Solve NCERT exercises thoroughly and then move to additional practice problems. Our Cubegon study materials provide structured practice sets that help you develop strong problem-solving skills.</p>
                      
                      <h5>Science (Physics, Chemistry, Biology)</h5>
                      <p>Science subjects demand both theoretical understanding and practical application. For Physics, focus on numerical problems and concept application. Chemistry requires understanding of reactions and mechanisms. Biology needs regular revision of diagrams and processes. Cubegon's comprehensive science resources make laboratory concepts easier to understand through visual aids and step-by-step explanations.</p>
                      
                      <h5>Social Studies</h5>
                      <p>Social Studies combines History, Geography, Civics, and Economics. Create timelines for historical events, use maps for geography, and understand current affairs for civics. Regular reading of newspapers helps in understanding contemporary issues. Our curated social studies content at Cubegon simplifies complex topics through interactive learning.</p>
                      
                      <h5>English</h5>
                      <p>English requires reading comprehension, writing skills, and grammar knowledge. Read extensively to improve vocabulary and comprehension. Practice writing essays, letters, and stories. Focus on grammar rules and their application. Cubegon's English resources include comprehensive grammar guides and writing practice exercises.</p>
                      
                      <h5>Hindi</h5>
                      <p>Hindi preparation involves literature understanding, grammar, and writing skills. Read prescribed texts thoroughly and understand themes and characters. Practice writing skills and focus on grammar rules. Our Hindi study materials at Cubegon provide detailed explanations of literary works and grammar concepts.</p>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="quick-tips-card">
                  <Card.Body>
                    <h4 className="tips-title">Quick Study Tips</h4>
                    <ul className="tips-list">
                      <li>Create a study schedule and stick to it</li>
                      <li>Take breaks every 45-50 minutes</li>
                      <li>Use active recall techniques</li>
                      <li>Teach others what you've learned</li>
                      <li>Use visual aids and diagrams</li>
                      <li>Practice previous year questions</li>
                      <li>Join study groups for discussion</li>
                      <li>Maintain a healthy lifestyle</li>
                      <li>Get adequate sleep (7-8 hours)</li>
                      <li>Stay hydrated and eat nutritious food</li>
                      <li>Explore Cubegon's interactive learning modules</li>
                      <li>Use our progress tracking features</li>
                    </ul>
                  </Card.Body>
                </Card>

                <Card className="cubegon-cta-card mt-4">
                  <Card.Body className="text-center">
                    <div className="cta-icon">
                      <img src={logo} alt="Cubegon" className="cta-logo" />
                    </div>
                    <h4 className="cta-title">Ready to Accelerate Your Learning?</h4>
                    <p className="cta-description2">
                      Join thousands of students excelling with Cubegon's platform.
                    </p>
                    <div className="cta-features">
                      <div className="feature-item">
                        <span className="feature-icon">📚</span>
                        <span className="feature-text">Study Materials</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">📊</span>
                        <span className="feature-text">Progress Tracking</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">🎯</span>
                        <span className="feature-text">Mock Tests</span>
                      </div>
                    </div>
                    <button 
                      className="btn btn-primary btn-lg w-100 mt-2"
                      onClick={() => window.open('https://cubegon.app', '_blank')}
                    >
                      Start Learning with Cubegon
                    </button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Exam Preparation Section */}
        <section className="exam-prep-section">
          <Container>
            <h2 className="section-title">Exam Preparation Strategies</h2>
            <Row>
              <Col md={6}>
                <Card className="prep-card">
                  <Card.Body>
                    <h4 className="prep-title">Pre-Exam Preparation (3-6 months before)</h4>
                    <ul className="prep-list">
                      <li><strong>Complete Syllabus:</strong> Ensure all topics are covered thoroughly using Cubegon's comprehensive study plans</li>
                      <li><strong>Regular Practice:</strong> Solve exercises and practice problems daily with our curated question banks</li>
                      <li><strong>Mock Tests:</strong> Take regular mock tests using Cubegon's assessment tools to track your progress</li>
                      <li><strong>Weak Areas:</strong> Identify and work on weak subjects/topics with targeted practice materials</li>
                      <li><strong>Revision:</strong> Regular revision of completed topics using our spaced repetition system</li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="prep-card">
                  <Card.Body>
                    <h4 className="prep-title">Last Month Preparation</h4>
                    <ul className="prep-list">
                      <li><strong>Final Revision:</strong> Focus on important topics and formulas using Cubegon's revision notes</li>
                      <li><strong>Sample Papers:</strong> Solve previous year papers under exam conditions with our timed practice sessions</li>
                      <li><strong>Time Management:</strong> Practice writing answers within time limits using our exam simulator</li>
                      <li><strong>Health:</strong> Maintain good health and avoid stress with our wellness tips</li>
                      <li><strong>Confidence:</strong> Stay positive and confident about your preparation with Cubegon's motivational content</li>
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Important Reminders Section */}
        <section className="reminders-section">
          <Container>
            <h2 className="section-title">Important Reminders</h2>
            <Row className="g-4">
              <Col md={6}>
                <Alert variant="success" className="reminder-alert">
                  <CheckCircle className="me-2" />
                  <strong>Success Tips:</strong>
                  <ul className="mt-2 mb-0">
                    <li>Consistency is key to success - study regularly</li>
                    <li>Regular revision helps retention and recall</li>
                    <li>Practice makes perfect - solve more problems</li>
                    <li>Stay positive and motivated throughout</li>
                    <li>Seek help when needed from teachers or peers</li>
                    <li>Maintain a balanced approach to all subjects</li>
                    <li>Use multiple learning methods for better understanding</li>
                  </ul>
                </Alert>
              </Col>
              <Col md={6}>
                <Alert variant="warning" className="reminder-alert">
                  <ExclamationTriangle className="me-2" />
                  <strong>Things to Avoid:</strong>
                  <ul className="mt-2 mb-0">
                    <li>Don't procrastinate - start early</li>
                    <li>Avoid last-minute cramming</li>
                    <li>Don't ignore any subject completely</li>
                    <li>Maintain healthy lifestyle - avoid burnout</li>
                    <li>Don't compare yourself with others constantly</li>
                    <li>Avoid excessive stress and anxiety</li>
                    <li>Don't skip meals or sleep for studies</li>
                  </ul>
                </Alert>
              </Col>
            </Row>
          </Container>
        </section>

        

        {/* Call to Action Section */}
        <section className="cta-section">
          <Container>
            <Card className="cta-card">
              <Card.Body className="text-center">
                <Award className="cta-icon mb-3" />
                <h3 className="cta-title">Ready to Excel in Class {grade}?</h3>
                <p className="cta-description">
                  Access our comprehensive study resources and start your journey towards academic success with Cubegon's proven learning methodology.
                </p>
                <div className="cta-buttons">
                  <button 
                    className="btn btn-primary btn-lg me-3"
                    onClick={() => navigate(`/class/${grade}`)}
                  >
                    View Study Resources
                  </button>
                  <button 
                    className="btn btn-outline-primary btn-lg"
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </button>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </section>
      </Container>

      {/* Footer */}
      <footer className="cubegon-footer">
        <div className="cubegon-footer-content">
          <div className="cubegon-footer-main">
            <div className="cubegon-footer-logo">
              <img src={whiteLogo} alt="Cubegon" />
            </div>

            <div className="cubegon-footer-download">
              <h3>Download the App</h3>
              <div className="cubegon-footer-qr-container">
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.cubegon.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={androidQrCode} alt="Android QR code - Download from Play Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>Android</p>
                  </a>
                </div>
                <div className="cubegon-footer-qr">
                  <a 
                    href="https://apps.apple.com/in/app/cubegon-smart-learning/id6752596017" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ display: 'block', textDecoration: 'none' }}
                  >
                    <img src={iosQrCode} alt="iOS QR code - Download from App Store" />
                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#fff' }}>iOS</p>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="cubegon-footer-bottom">
            <div className="cubegon-footer-social">
              <a aria-label="Facebook" href="https://facebook.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Facebook />
              </a>
              <a aria-label="Twitter" href="https://twitter.com/cubegon" target="_blank" rel="noopener noreferrer">
                <Twitter />
              </a>
              <a aria-label="Instagram" href="https://instagram.com/cubegon.app" target="_blank" rel="noopener noreferrer">
                <Instagram />
              </a>
              <a aria-label="LinkedIn" href="https://linkedin.com/company/cubegon" target="_blank" rel="noopener noreferrer">
                <Linkedin />
              </a>
            </div>

            <p className="cubegon-footer-text">Made in 🇮🇳 with ❤️</p>
            <p className="cubegon-footer-copyright">© {new Date().getFullYear()} Numeric Education Pvt. Ltd. All rights reserved.</p>
            <div className="cubegon-footer-links">
              <a href="https://cubegon.app/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://cubegon.app/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClassGuide;
